import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CrossReferenceService from '@/services/CatalogServices/CrossReferenceService';
import { extractError } from '@/helpers/extractApiError';
import { removeSelectedItem, replaceStatusChangedItem } from '@/helpers/CRUD';
import { deleteCrossReference } from '../delete/deleteSlice';

const model = {
    data: [],
    totalItems: 0,
    loading: false,
    error: null,
    successOnCrossReferenceStatusChange: '',
    xlsxUrl: null,
    loadingExport: false,
};

export const getProductCrossReferenceList = createAsyncThunk(
    'cross-reference/getProductCrossReferenceList',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CrossReferenceService.getProductCrossReferenceList(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const changeCrossReferenceStatus = createAsyncThunk(
    'cross-reference/changeCrossReferenceStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CrossReferenceService.changeCrossReferenceStatus(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const exportCrossReference = createAsyncThunk(
    'cross-reference/exportCrossReference',
    async (_, { rejectWithValue }) => {
        try {
            const response = await CrossReferenceService.exportCrossReference();
            const { file_url: xlsxUrl } = response.data;
            return xlsxUrl;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const crossReferenceListSlice = createSlice({
    name: 'productCrossReferenceList',
    initialState: model,
    reducers: {
        resetChangeCrossReferenceStatusStateOnSuccess: (state) => {
            state.successOnCrossReferenceStatusChange = '';
        },
        resetChangeCrossReferenceStatusStateOnError: (state) => {
            state.error = null;
        },
        resetCrossReferenceDownloadState: (state) => {
            state.xlsxUrl = null;
        },
    },
    extraReducers: (builder) => {
        // product cross reference list
        builder.addCase(getProductCrossReferenceList.pending, (state) => {
            state.loading = true;
        }).addCase(getProductCrossReferenceList.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.data;
            state.totalItems = action.payload.total;
        }).addCase(getProductCrossReferenceList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change cross reference status
        builder.addCase(changeCrossReferenceStatus.pending, (state) => {
            state.loading = true;
        }).addCase(changeCrossReferenceStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.successOnCrossReferenceStatusChange = 'Cross reference status changed successfully!';
            state.data = replaceStatusChangedItem(
                state.data,
                action.payload.id,
                action.payload.status,
            );
        }).addCase(changeCrossReferenceStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Handle deleting customer
        builder.addCase(deleteCrossReference.fulfilled, (state, action) => {
            state.data = removeSelectedItem(state.data, action.payload.crossReferenceId);
            state.totalItems -= 1;
        });
        // export cross reference
        builder.addCase(exportCrossReference.pending, (state) => {
            state.loadingExport = true;
        });
        builder.addCase(exportCrossReference.fulfilled, (state, action) => {
            state.loadingExport = false;
            state.xlsxUrl = action.payload;
        });
        builder.addCase(exportCrossReference.rejected, (state, action) => {
            state.loadingExport = false;
            state.error = extractError(action);
        });
    },
});

export const {
    resetChangeCrossReferenceStatusStateOnSuccess,
    resetChangeCrossReferenceStatusStateOnError,
    resetCrossReferenceDownloadState,
} = crossReferenceListSlice.actions;

export default crossReferenceListSlice.reducer;
